import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import useDataContext from 'data/context/data';

import { bundleComparison } from '../../lib/displayPrices.mjs';
import { money } from '../../lib/landingPrice';
import { ProductDetailLink } from '../pageLinks';
import PdpIcon from '../product/pdpIcon';
import { Picture, Responsive, SectionHead, imageShape } from '../landing/shared';

// Print + display bundles (one backend SKU each). The set is picked on the print's PDP, so the
// card links there. Prices are the bundle's own, compared like the PDP bundle picker. A set the
// catalog does not sell is left out.
const DisplaySets = ({ id, sets, track }) => {
  const dataContext = useDataContext();
  const priceOf = (skuId) => dataContext.getSkuProduct(skuId)?.price ?? null;

  const cards = sets
    .map((set) => ({
      ...set,
      bundle: dataContext.getSkuProduct(set.bundleId),
      print: dataContext.getProduct(set.printId),
    }))
    .filter(({ bundle, print }) => bundle && print);

  return (
    <section id={id} className="lnd-section dsp-sets">
      <Container>
        <SectionHead
          eyebrow="Print + display sets"
          title="The print and the holder, in one order"
          aside={
            <Responsive
              desktop="Pick a set on the print’s page. The set costs less than the print and the accessory bought separately."
              phone="Pick a set on the print’s page. It costs less than buying both separately."
            />
          }
        />
        <ul className="dsp-set-list">
          {cards.map(({ bundle, print, title, includes, image }) => {
            const comparison = bundleComparison(bundle, priceOf);
            return (
              <li key={bundle.id}>
                <ProductDetailLink productId={print.id}>
                  <a className="dsp-set" onClick={track('set', 'sets', bundle.id)}>
                    <span className="dsp-set-photo">
                      <Picture image={image} sizes="(min-width: 992px) 282px, 264px" alt="" />
                    </span>
                    <span className="dsp-set-body">
                      <span>
                        <span className="dsp-set-title">{title}</span>
                        <span className="dsp-set-includes">{includes}</span>
                      </span>
                      <span className="dsp-set-bottom">
                        <span className="dsp-set-prices">
                          <span className="dsp-set-price">
                            {money(bundle.price, bundle.currency)}
                          </span>
                          {comparison?.savings && (
                            <>
                              <span className="dsp-set-separately">
                                {`${money(comparison.separately, bundle.currency)} separately`}
                              </span>
                              <span className="dsp-set-save">
                                {`Save ${money(comparison.savings, bundle.currency)}`}
                              </span>
                            </>
                          )}
                        </span>
                        <span className="dsp-set-cta">
                          Shop the set
                          <PdpIcon name="arrowRight" size={15} />
                        </span>
                      </span>
                    </span>
                  </a>
                </ProductDetailLink>
              </li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
};

DisplaySets.propTypes = {
  id: PropTypes.string.isRequired,
  sets: PropTypes.arrayOf(
    PropTypes.shape({
      bundleId: PropTypes.string.isRequired,
      printId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      includes: PropTypes.string.isRequired,
      image: imageShape.isRequired,
    }),
  ).isRequired,
  track: PropTypes.func.isRequired,
};

export default DisplaySets;
